"use client";

import posthog from "posthog-js/dist/module.full.no-external";
import type { PostHogInterface } from "posthog-js/dist/module.full.no-external";
import {
  clientApiKey,
  createPosthogConfig,
  shouldDisablePosthogSessionRecording,
} from "./config";
import {
  phConsumeQueuedCaptures,
  phConsumeQueuedIdentity,
  phConsumeQueuedRegistration,
} from "./queue";

interface InitializePosthogClientOptions {
  pathname?: string | null;
}

let posthogClient: PostHogInterface | null = null;
let hasInitialized = false;

function handlePosthogLoaded(posthogInstance: PostHogInterface): void {
  posthogClient = posthogInstance;
  window.posthog = posthogInstance;
  window.__posthogInitialized = true;

  const queuedIdentity = phConsumeQueuedIdentity();
  if (queuedIdentity) {
    posthogInstance.identify(queuedIdentity.id, queuedIdentity.props);
  }

  const queuedReg = phConsumeQueuedRegistration();
  if (queuedReg) {
    if (Object.keys(queuedReg.registerOnce).length > 0) {
      posthogInstance.register_once(queuedReg.registerOnce);
    }
    if (Object.keys(queuedReg.register).length > 0) {
      posthogInstance.register(queuedReg.register);
    }
  }

  const queuedCaptures = phConsumeQueuedCaptures();
  if (queuedCaptures.length > 0) {
    for (const queuedCapture of queuedCaptures) {
      posthogInstance.capture(
        queuedCapture.event,
        queuedCapture.properties,
        queuedCapture.options,
      );
    }
  }
}

export function applyPosthogSessionRecordingPolicy(
  pathname?: string | null,
): void {
  if (typeof window === "undefined") return;

  const shouldDisableRecording = shouldDisablePosthogSessionRecording(pathname);
  const client = (posthogClient ?? window.posthog) as
    | Partial<PostHogInterface>
    | undefined;
  if (!client) return;

  try {
    client.set_config?.({ disable_session_recording: shouldDisableRecording });
    if (shouldDisableRecording) {
      client.stopSessionRecording?.();
    }
  } catch {
  }
}

export function initializePosthogClient(
  options: InitializePosthogClientOptions = {},
): void {
  if (typeof window === "undefined") return;
  if (process.env.NODE_ENV === "development") return;
  if (!clientApiKey) return;

  if (window.__posthogInitialized || hasInitialized) {
    hasInitialized = true;
    applyPosthogSessionRecordingPolicy(options.pathname);
    return;
  }

  try {
    posthog.init(clientApiKey, {
      ...createPosthogConfig(options.pathname),
      loaded: handlePosthogLoaded,
    });
    hasInitialized = true;
  } catch (error) {
    console.error("[PostHog] Initialization failed:", error);
  }
}
